/** Responsive layout and browser fullscreen are independent of weather fetching. */
import { computed, ref, onScopeDispose } from "vue";
import { defineStore } from "pinia";

export const useDisplayStore = defineStore("display", () => {
  const fitsWall = ref(false);
  const tabletMode = ref(false);
  const fullscreen = ref(false);
  const wallMode = computed(
    () => tabletMode.value || (fitsWall.value && fullscreen.value),
  );
  let cleanups: Array<() => void> = [];

  /** Register viewport and fullscreen listeners once for the app shell. */
  function start() {
    if (cleanups.length) return;

    const size = window.matchMedia("(min-width: 768px)");
    const tablet = window.matchMedia("(min-width: 768px) and (max-width: 1279px)");
    const full = window.matchMedia("(display-mode: fullscreen)");

    // Sync wall mode from browser state without touching weather requests.
    const sync = () => {
      fitsWall.value = size.matches;
      tabletMode.value = tablet.matches;
      fullscreen.value = !!document.fullscreenElement || full.matches;
    };
    for (const query of [size, tablet, full]) {
      query.addEventListener("change", sync);
      cleanups.push(() => query.removeEventListener("change", sync));
    }
    document.addEventListener("fullscreenchange", sync);
    cleanups.push(() => document.removeEventListener("fullscreenchange", sync));
    sync();
  }

  /** Remove listeners when the store scope ends. */
  function stop() {
    cleanups.forEach((cleanup) => cleanup());
    cleanups = [];
  }

  /** Toggle browser fullscreen; tablet layout works even if it is denied. */
  async function toggleFullscreen() {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen?.();
    } catch {
      // Tablet layout remains available when the browser rejects fullscreen.
    }
  }

  onScopeDispose(stop);

  return { fitsWall, tabletMode, fullscreen, wallMode, start, stop, toggleFullscreen };
});
